import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Button,
  Box,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  SelectChangeEvent,
  Grid,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Divider,
  IconButton,
  Switch,
  CircularProgress,
  Snackbar,
  Alert,
  FormHelperText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import axiosInstance from '../axiosInstance';
import { ArrowBack, Delete, Add, Compare } from '@mui/icons-material';
import { Check as CheckIcon, Close as CloseIcon } from '@mui/icons-material';
import { isUserAdmin } from '../../utils/userUtils';


const EDITABLE_FIELDS = [
  { id: 'status', label: 'Status', type: 'select', group: 'main' },
  { id: 'deviceType', label: 'Device Type', type: 'select', optionsKey: 'Device Type', group: 'main' },
  { id: 'location', label: 'Location', type: 'select', group: 'main' },
  { id: 'versionOfDz', label: 'Version of DZ', type: 'select', optionsKey: 'Version of DZ', group: 'main' },
  { id: 'powerSupplyType', label: 'Power Supply Type', type: 'select', optionsKey: 'Power Supply Type', group: 'main' },
  { id: 'measurementTrackType', label: 'Measurement Track Type', type: 'select', optionsKey: 'Measurement Track Type', group: 'main' },
  { id: 'externalMeteo', label: 'External Meteo', type: 'select', optionsKey: 'External Meteo', group: 'main' },
  { id: 'pullUp', label: 'Pull Up', type: 'switch', group: 'switches' },
  { id: 'rtcBattery', label: 'RTC Battery', type: 'switch', group: 'switches' },
  { id: 'voltageFilter', label: 'Voltage Filter', type: 'switch', group: 'switches' },
  { id: 'sensors', label: 'Sensors', type: 'array', group: 'arrays' },
  { id: 'antennas', label: 'Antennas', type: 'array', group: 'arrays' },
];

const initialValues = {
  sensors: [],
  antennas: [],
  pullUp: false,
  rtcBattery: false,
  voltageFilter: false,
};

const BulkEditDevices: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.name === 'dark';
  const [activeStep, setActiveStep] = useState(0);
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [options, setOptions] = useState<Record<string, string[]>>({});
  const [values, setValues] = useState<Record<string, any>>(initialValues);
  const [deviceNumbers, setDeviceNumbers] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [missingBoxes, setMissingBoxes] = useState<number[]>([]);
  const [boxDetails, setBoxDetails] = useState<any[]>([]);
  const [isCompareModalOpen, setIsCompareModalOpen] = useState(false);
  const [changes, setChanges] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetchOptions();
  }, []);

  const fetchOptions = async () => {
    try {
      const response = await axiosInstance.get('/itemsmanager/forms/sensorbox');
      setOptions(response.data);
    } catch (error) {
      console.error('Error fetching options:', error);
    }
  };

  const handleFieldSelection = (field: string) => {
    if (field === 'status' && !isUserAdmin()) {
      setSnackbar({
        open: true,
        message: 'Only administrators can modify the status field',
        severity: 'error'
      });
      return;
    }

    setSelectedFields(prev => {
      const newFields = prev.includes(field) 
        ? prev.filter(f => f !== field)
        : [...prev, field];
      
      if (prev.includes(field)) {
        setValues(currentValues => {
          const { [field]: removed, ...rest } = currentValues;
          return rest;
        });
      } else if (EDITABLE_FIELDS.find(f => f.id === field)?.type === 'switch') {
        setValues(currentValues => ({
          ...currentValues,
          [field]: false
        }));
      }
      
      return newFields;
    });
  };

  const handleValueChange = (field: string, value: any) => {
    setValues(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNext = () => {
    let isValid = true;
    
    switch (activeStep) {
      case 0:
        isValid = validateStep1();
        break;
      case 1:
        isValid = validateStep2();
        break;
    }

    if (isValid) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep3()) {
      return;
    }

    setIsSubmitting(true);
    const deviceList = deviceNumbers.split(/[\s,]+/).filter(n => n);
    
    // Prepare fields_to_update object
    const fields_to_update: Record<string, any> = {};
    
    // Add non-array fields
    selectedFields
      .filter(field => !['sensors', 'antennas'].includes(field))
      .forEach(field => {
        if (values[field] !== undefined) {
          // Convert camelCase to snake_case for API
          const apiField = field.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
          
          // Convert boolean values to strings for switches
          const fieldConfig = EDITABLE_FIELDS.find(f => f.id === field);
          if (fieldConfig?.type === 'switch') {
            fields_to_update[apiField] = values[field] ? "true" : "false";
          } else {
            fields_to_update[apiField] = values[field];
          }
        }
      });
    
    // Add arrays if selected
    if (selectedFields.includes('sensors')) {
      fields_to_update.sensors = values.sensors || [];
    }
    if (selectedFields.includes('antennas')) {
      fields_to_update.antennas = values.antennas || [];
    }

    // Prepare box_specific_data object
    const box_specific_data: Record<string, any> = {};
    
    deviceList.forEach(deviceNumber => {
      if (selectedFields.includes('sensors') && values.sensors?.length > 0) {
        box_specific_data[deviceNumber] = {
          number_of_sensors: {},
          number_of_channels: {},
          number_of_slots: {}
        };

        values.sensors.forEach((_, index) => {
          const serialNumber = values[`device_${deviceNumber}_sensor_${index}_serial`];
          const channels = values[`device_${deviceNumber}_sensor_${index}_channels`];
          const slots = values[`device_${deviceNumber}_sensor_${index}_slot`];

          if (serialNumber) {
            box_specific_data[deviceNumber].number_of_sensors[`number_of_sensor_${index + 1}`] = serialNumber;
          }
          if (channels) {
            box_specific_data[deviceNumber].number_of_channels[`number_of_channel_${index + 1}`] = channels;
          }
          if (slots) {
            box_specific_data[deviceNumber].number_of_slots[`number_of_slot_${index + 1}`] = slots;
          }
        });
      }
    });

    const payload = {
      boxes: deviceList.map(Number),
      fields_to_update,
      box_specific_data
    };

    try {
      const response = await axiosInstance.put('/boxpanel/boxes/bulk-update', payload);
      navigate('/', { 
        state: { 
          successMessage: `Successfully updated ${deviceList.length} device${deviceList.length > 1 ? 's' : ''}`
        }
      });
    } catch (error: any) {
      console.error('Error during bulk update:', error);
      if (error.response?.data?.missing_boxes) {
        setMissingBoxes(error.response.data.missing_boxes);
      }
      const errorMessage = error.response?.data?.message || 'An unexpected error occurred';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
      setIsSubmitting(false);
    }
  };

  const handleSensorChange = (index: number, value: string) => {
    const updatedSensors = [...(values.sensors || [])];
    updatedSensors[index] = value;
    setValues(prev => ({
      ...prev,
      sensors: updatedSensors,
    }));
  };

  const handleAddSensor = () => {
    setValues(prev => ({
      ...prev,
      sensors: [...(prev.sensors || []), ''],
      numberOfSensors: [...(prev.numberOfSensors || []), ''],
      numberOfChannels: [...(prev.numberOfChannels || []), ''],
      numberOfSlots: [...(prev.numberOfSlots || []), ''],
    }));
  };

  const handleRemoveSensor = (index: number) => {
    const filteredSensors = values.sensors.filter((_: any, i: number) => i !== index);
    const filteredNumbers = values.numberOfSensors.filter((_: any, i: number) => i !== index);
    const filteredChannels = values.numberOfChannels.filter((_: any, i: number) => i !== index);
    const filteredSlots = values.numberOfSlots.filter((_: any, i: number) => i !== index);
    setValues(prev => ({
      ...prev,
      sensors: filteredSensors,
      numberOfSensors: filteredNumbers,
      numberOfChannels: filteredChannels,
      numberOfSlots: filteredSlots,
    }));
  };

  const handleAntennaChange = (index: number, value: string) => {
    const updatedAntennas = [...(values.antennas || [])];
    updatedAntennas[index] = value;
    setValues(prev => ({
      ...prev,
      antennas: updatedAntennas,
    }));
  };

  const handleAddAntenna = () => {
    setValues(prev => ({
      ...prev,
      antennas: [...(prev.antennas || []), ''],
    }));
  };

  const handleRemoveAntenna = (index: number) => {
    const filteredAntennas = values.antennas.filter((_: any, i: number) => i !== index);
    setValues(prev => ({
      ...prev,
      antennas: filteredAntennas,
    }));
  };

  const steps = ['Select Fields', 'Set Values', 'Choose Devices'];

  const paperStyle = {
    p: 3,
    backgroundColor: isDark ? 'background.paper' : 'background.default',
    border: isDark ? '1px solid #525252' : '1px solid rgba(0, 0, 0, 0.12)',
    borderRadius: 8,
    transition: 'all 0.3s ease-in-out',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
    '&:hover': {
      boxShadow: isDark ? '0 0 12px rgba(255, 255, 255, 0.2)' : '0 0 12px rgba(0, 0, 0, 0.2)',
    }
  };

  const fieldCardStyle = {
    p: 1.5,
    cursor: 'pointer',
    bgcolor: 'background.secondary',
    transition: 'all 0.2s ease-in-out',
    border: '1px solid',
    borderColor: 'transparent',
    '&:hover': {
      transform: 'translateY(-2px)',
      borderColor: 'primary.main',
    },
    '&.selected': {
      bgcolor: isDark ? 'background.paper' : 'primary.light',
      borderColor: 'primary.main',
      boxShadow: isDark ? '0 0 5px rgba(100, 181, 246, 0.4)' : '0 0 5px rgba(25, 118, 210, 0.4)',
    }
  };

  const gridItemStyle = {
    mb: 1,
  };

  const renderFieldCard = (field: any) => (
    <Grid item xs={12} key={field.id} sx={gridItemStyle}>
      <Paper 
        sx={{
          ...fieldCardStyle,
          ...(selectedFields.includes(field.id) && {
            '&.selected': fieldCardStyle['&.selected']
          })
        }}
        className={selectedFields.includes(field.id) ? 'selected' : ''}
        onClick={() => handleFieldSelection(field.id)}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>{field.label}</Typography>
            <Typography variant="caption" color="text.secondary">
              {field.type === 'select' ? 'Dropdown selection' : 
               field.type === 'switch' ? 'Toggle option' : 
               field.type === 'array' ? 'Multiple items' : 'Text input'}
            </Typography>
          </Box>
          {selectedFields.includes(field.id) && (
            <Chip
              label="Selected"
              size="small"
              color="primary"
              sx={{ 
                borderRadius: 0,
                height: '24px',
              }}
            />
          )}
        </Box>
      </Paper>
    </Grid>
  );

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={6}>
              <Paper sx={paperStyle}>
                <Typography variant="h6" gutterBottom sx={{ mb: 1 }}>
                  Register Sensor Box
                </Typography>
                <Grid container spacing={1}>
                  {EDITABLE_FIELDS
                    .filter(field => field.group === 'main')
                    .map(renderFieldCard)}
                </Grid>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper sx={paperStyle}>
                <Typography variant="h6" gutterBottom sx={{ mb: 1 }}>
                  Switches
                </Typography>
                <Grid container spacing={1}>
                  {EDITABLE_FIELDS
                    .filter(field => field.group === 'switches')
                    .map(renderFieldCard)}
                </Grid>
              </Paper>

              <Paper sx={{ ...paperStyle, mt: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ mb: 1 }}>
                  Sensors & Antennas
                </Typography>
                <Grid container spacing={1}>
                  {EDITABLE_FIELDS
                    .filter(field => field.group === 'arrays')
                    .map(renderFieldCard)}
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        );

      case 1:
        return (
          <Grid container spacing={1.5}>
            <Grid item xs={12}>
              <Paper sx={paperStyle}>
                <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                  Set Values
                </Typography>
                
                {/* Main select fields */}
                <Grid container spacing={2}>
                  {selectedFields
                    .filter(field => !['sensors', 'antennas'].includes(field) && 
                      EDITABLE_FIELDS.find(f => f.id === field)?.type !== 'switch')
                    .map((field) => (
                      <Grid item xs={12} sm={6} md={4} key={field}>
                        {renderField(field, EDITABLE_FIELDS.find(f => f.id === field))}
                      </Grid>
                    ))}
                </Grid>

                {/* Switches section */}
                {selectedFields.some(field => EDITABLE_FIELDS.find(f => f.id === field)?.type === 'switch') && (
                  <>
                    <Typography variant="subtitle1" sx={{ mt: 4, mb: 2 }}>
                      Toggle Options
                    </Typography>
                    <Grid container spacing={2}>
                      {selectedFields
                        .filter(field => EDITABLE_FIELDS.find(f => f.id === field)?.type === 'switch')
                        .map((field) => (
                          <Grid item xs={12} sm={6} md={4} key={field}>
                            <Paper 
                              sx={{ 
                                p: 2,
                                bgcolor: 'background.secondary'
                              }}
                            >
                              {renderField(field, EDITABLE_FIELDS.find(f => f.id === field))}
                            </Paper>
                          </Grid>
                        ))}
                    </Grid>
                  </>
                )}

                {/* Sensors and Antennas section */}
                {(selectedFields.includes('sensors') || selectedFields.includes('antennas')) && (
                  <Box sx={{ mt: 4 }}>
                    <Divider sx={{ mb: 3 }} />
                    <Grid container spacing={3}>
                      {selectedFields.includes('sensors') && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle1" gutterBottom sx={{ mb: 2 }}>
                            Sensors
                          </Typography>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {values.sensors?.map((sensor: string, index: number) => (
                              <Paper 
                                key={index} 
                                sx={{ 
                                  p: 2,
                                  bgcolor: 'background.secondary'
                                }}
                              >
                                <Box sx={{ 
                                  display: 'flex', 
                                  gap: 1,
                                  alignItems: 'center'
                                }}>
                                  <FormControl size="small" sx={{ flex: 1 }}>
                                    <InputLabel>Sensor Type</InputLabel>
                                    <Select
                                      value={sensor}
                                      onChange={(e) => handleSensorChange(index, e.target.value)}
                                      label="Sensor Type"
                                    >
                                      {options.Sensors?.map((option: string) => (
                                        <MenuItem key={option} value={option}>
                                          {option}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                  <IconButton 
                                    onClick={() => handleRemoveSensor(index)}
                                  >
                                    <Delete />
                                  </IconButton>
                                </Box>
                              </Paper>
                            ))}
                            <Button
                              variant="outlined"
                              onClick={handleAddSensor}
                              startIcon={<Add />}
                              sx={{ alignSelf: 'flex-start' }}
                            >
                              Add Sensor
                            </Button>
                          </Box>
                        </Grid>
                      )}

                      {selectedFields.includes('antennas') && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle1" gutterBottom sx={{ mb: 2 }}>
                            Antennas
                          </Typography>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {values.antennas?.map((antenna: string, index: number) => (
                              <Paper 
                                key={index} 
                                sx={{ 
                                  p: 2,
                                  bgcolor: 'background.secondary'
                                }}
                              >
                                <Box sx={{ 
                                  display: 'flex', 
                                  gap: 1,
                                  alignItems: 'center'
                                }}>
                                  <FormControl size="small" sx={{ flex: 1 }}>
                                    <InputLabel>Antenna Type</InputLabel>
                                    <Select
                                      value={antenna}
                                      onChange={(e) => handleAntennaChange(index, e.target.value)}
                                      label="Antenna Type"
                                    >
                                      {options.Antennas?.map((option: string) => (
                                        <MenuItem key={option} value={option}>
                                          {option}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                  <IconButton 
                                    onClick={() => handleRemoveAntenna(index)}
                                  >
                                    <Delete />
                                  </IconButton>
                                </Box>
                              </Paper>
                            ))}
                            <Button
                              variant="outlined"
                              onClick={handleAddAntenna}
                              startIcon={<Add />}
                              sx={{ alignSelf: 'flex-start' }}
                            >
                              Add Antenna
                            </Button>
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        );

      case 2:
        const deviceList = deviceNumbers.split(/[\s,]+/).filter(n => n);
        return (
          <Box sx={{ '& > *': { mb: 1.5 } }}>
            <TextField
              fullWidth
              label="Device Numbers"
              value={deviceNumbers}
              onChange={(e) => {
                setDeviceNumbers(e.target.value);
                setMissingBoxes([]);
              }}
              placeholder="Enter device numbers (separated by spaces or commas)"
              multiline
              rows={3}
              size="small"
              error={deviceNumbers.trim() === ''}
              helperText={deviceNumbers.trim() === '' ? 'Please enter at least one device number' : ''}
            />
            
            {deviceList.length > 0 && selectedFields.includes('sensors') && (
              <Paper sx={paperStyle}>
                <Typography variant="h6" gutterBottom>
                  Sensor Details per Device
                </Typography>
                <Grid container spacing={2}>
                  {deviceList.map((deviceNumber) => (
                    <Grid item xs={12} sm={6} md={4} key={deviceNumber}>
                      <Paper 
                        sx={{
                          p: 2,
                          bgcolor: 'background.secondary',
                          border: '1px solid',
                          borderColor: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
                        }}
                      >
                        <Typography 
                          variant="subtitle1" 
                          gutterBottom 
                          sx={{ 
                            borderBottom: 1, 
                            borderColor: 'divider',
                            pb: 1,
                            mb: 2,
                            fontWeight: 500
                          }}
                        >
                          Device #{deviceNumber}
                        </Typography>
                        {values.sensors?.map((sensor: string, index: number) => (
                          <Box key={index} sx={{ mb: 2 }}>
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                display: 'block',
                                mb: 1,
                                color: 'text.secondary',
                                fontWeight: 500
                              }}
                            >
                              {sensor}
                            </Typography>
                            <Box 
                              sx={{ 
                                display: 'flex', 
                                gap: 1,
                                alignItems: 'center'
                              }}
                            >
                              <TextField
                                size="small"
                                label="S/N"
                                inputProps={{ maxLength: 6 }}
                                sx={{ 
                                  width: '100px',
                                  '& input': { 
                                    textAlign: 'center',
                                    fontFamily: 'monospace'
                                  }
                                }}
                                value={values[`device_${deviceNumber}_sensor_${index}_serial`] || ''}
                                onChange={(e) => handleValueChange(
                                  `device_${deviceNumber}_sensor_${index}_serial`,
                                  e.target.value.toUpperCase()
                                )}
                              />
                              <TextField
                                size="small"
                                label="CH"
                                inputProps={{ 
                                  maxLength: 1,
                                  type: 'number',
                                  min: 1,
                                  max: 9
                                }}
                                sx={{ 
                                  width: '70px',
                                  '& input': { 
                                    textAlign: 'center',
                                    fontFamily: 'monospace'
                                  }
                                }}
                                value={values[`device_${deviceNumber}_sensor_${index}_channels`] || ''}
                                onChange={(e) => handleValueChange(
                                  `device_${deviceNumber}_sensor_${index}_channels`,
                                  e.target.value
                                )}
                              />
                              <TextField
                                size="small"
                                label="SLOT"
                                inputProps={{ 
                                  maxLength: 1,
                                  type: 'number',
                                  min: 1,
                                  max: 9
                                }}
                                sx={{ 
                                  width: '70px',
                                  marginLeft: '8px',
                                  '& input': { 
                                    textAlign: 'center',
                                    fontFamily: 'monospace'
                                  }
                                }}
                                value={values[`device_${deviceNumber}_sensor_${index}_slot`] || ''}
                                onChange={(e) => handleValueChange(
                                  `device_${deviceNumber}_sensor_${index}_slot`,
                                  e.target.value
                                )}
                              />
                            </Box>
                          </Box>
                        ))}
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            )}
          </Box>
        );
    }
  };

  const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar(prev => ({
      ...prev,
      open: false
    }));
  };

  const validateStep1 = () => {
    if (selectedFields.length === 0) {
      setSnackbar({
        open: true,
        message: 'Please select at least one field to edit',
        severity: 'error'
      });
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    const newErrors: Record<string, string> = {};
    let isValid = true;

    selectedFields.forEach(field => {
      const fieldConfig = EDITABLE_FIELDS.find(f => f.id === field);
      
      if (fieldConfig?.type === 'array') {
        // Walidacja dla sensors i antennas
        if (values[field]?.length === 0) {
          newErrors[field] = `Please add at least one ${field.slice(0, -1)}`; // removes 's' from end
          isValid = false;
        } else if (values[field]?.some((item: string) => !item)) {
          newErrors[field] = `All ${field} must have a type selected`;
          isValid = false;
        }
      } else if (fieldConfig?.type === 'select') {
        // Walidacja dla pól select
        if (!values[field]) {
          newErrors[field] = `Please select a ${fieldConfig.label}`;
          isValid = false;
        }
      }
    });

    setErrors(newErrors);
    
    if (!isValid) {
      setSnackbar({
        open: true,
        message: 'Please fill in all required fields',
        severity: 'error'
      });
    }
    
    return isValid;
  };

  const validateStep3 = () => {
    const deviceList = deviceNumbers.split(/[\s,]+/).filter(n => n);
    
    if (deviceList.length === 0) {
      setSnackbar({
        open: true,
        message: 'Please enter at least one device number',
        severity: 'error'
      });
      return false;
    }

    // Walidacja czy wszystkie wymagane pola są wypełnione dla sensorów
    if (selectedFields.includes('sensors')) {
      for (const deviceNumber of deviceList) {
        for (let i = 0; i < values.sensors.length; i++) {
          const serialKey = `device_${deviceNumber}_sensor_${i}_serial`;
          const channelKey = `device_${deviceNumber}_sensor_${i}_channels`;
          const slotKey = `device_${deviceNumber}_sensor_${i}_slot`;
          
          if (!values[serialKey]) {
            setSnackbar({
              open: true,
              message: `Device #${deviceNumber}: Serial number is required for sensor ${i + 1}`,
              severity: 'error'
            });
            return false;
          }
          
          if (!values[channelKey]) {
            setSnackbar({
              open: true,
              message: `Device #${deviceNumber}: Channel is required for sensor ${i + 1}`,
              severity: 'error'
            });
            return false;
          }
          
          if (!values[slotKey]) {
            setSnackbar({
              open: true,
              message: `Device #${deviceNumber}: Slot is required for sensor ${i + 1}`,
              severity: 'error'
            });
            return false;
          }
        }
      }
    }

    return true;
  };

  const renderField = (field: string, fieldConfig: any) => {
    const isStatusField = field === 'status';
    const isDisabled = isStatusField && !isUserAdmin();

    if (fieldConfig?.type === 'switch') {
      return (
        <FormControl 
          error={!!errors[field]}
          sx={{ width: '100%' }}
        >
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
          }}>
            <Typography variant="body2">{fieldConfig.label}</Typography>
            <Switch
              size="small"
              checked={values[field] || false}
              onChange={(e) => handleValueChange(field, e.target.checked)}
              color="primary"
            />
          </Box>
          {errors[field] && (
            <FormHelperText>{errors[field]}</FormHelperText>
          )}
        </FormControl>
      );
    }

    return (
      <FormControl 
        fullWidth 
        size="small"
        error={!!errors[field]}
      >
        <InputLabel>{fieldConfig?.label}</InputLabel>
        <Select
          value={values[field] || ''}
          onChange={(e) => handleValueChange(field, e.target.value)}
          label={fieldConfig?.label}
          disabled={isDisabled}
        >
          {options[fieldConfig?.optionsKey || fieldConfig?.label]?.map((option: string) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
        {errors[field] && (
          <FormHelperText>{errors[field]}</FormHelperText>
        )}
      </FormControl>
    );
  };

  // Define the correct field names as expected by the backend
  const correctFieldNames = {
    deviceType: 'device_type',
    powerSupplyType: 'power_supply_type',
    versionOfDz: 'version_of_dz',
    rtcBattery: 'rtc_battery',
    measurementTrackType: 'measurement_track_type',
    externalMeteo: 'external_meteo',
    voltageFilter: 'voltage_filter',
    pullUp: 'pull_up',
    // Add other fields if necessary
  };

  // When preparing the fields for the request, map them to the correct names
  const fetchBoxDetails = async () => {
    const deviceList = deviceNumbers.split(/[\s,]+/).filter(n => n);
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      deviceList.forEach(box => params.append('boxes', box));
      selectedFields.forEach(field => {
        const correctFieldName = correctFieldNames[field] || field;
        params.append('fields', correctFieldName);
      });

      const response = await axiosInstance.get(`/boxpanel/boxes/details?${params.toString()}`);
      setBoxDetails(response.data.boxes);
      compareChanges(response.data.boxes);
      setIsCompareModalOpen(true);
    } catch (error: any) {
      console.error('Error fetching box details:', error);
      const errorMessage = error.response?.data?.message || 'Failed to fetch current box details';
      const missingBoxes = error.response?.data?.missing_boxes || [];
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });

      if (missingBoxes.length > 0) {
        setMissingBoxes(missingBoxes);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const compareChanges = (currentBoxes: any[]) => {
    const changesData = currentBoxes.map(box => {
      const changesForBox: Record<string, any> = { box: box.box };
      selectedFields.forEach(field => {
        const backendFieldName = correctFieldNames[field] || field;
        let currentValue = box[backendFieldName];
        let newValue = values[field];

        // Handle switch/boolean values
        if (EDITABLE_FIELDS.find(f => f.id === field)?.type === 'switch') {
          // Convert both values to lowercase strings for comparison
          currentValue = String(currentValue).toLowerCase();
          newValue = newValue ? "true" : "false";
          
          console.log('Switch field comparison:', {
            field,
            currentValueType: typeof currentValue,
            currentValue,
            newValueType: typeof newValue,
            newValue,
            areEqual: currentValue === newValue
          });
        }

        // Compare values
        const areValuesEqual = currentValue === newValue;

        console.log('Detailed comparison:', {
          field,
          currentValue,
          newValue,
          areEqual: areValuesEqual
        });

        if (!areValuesEqual) {
          changesForBox[field] = {
            from: currentValue,
            to: newValue
          };
        }
      });

      console.log('Changes for box:', changesForBox);

      // Only return boxes that have actual changes
      const hasChanges = Object.keys(changesForBox).length > 1;
      console.log('Has changes:', hasChanges);
      
      return hasChanges ? changesForBox : null;
    }).filter(Boolean);

    console.log('Final changes data:', changesData);
    setChanges(changesData);
  };

  // Funkcja do porównywania sensorów
  const compareSensors = (currentSensors: any, newSensors: string[]) => {
    // Ensure currentSensorValues is an array of strings
    const currentSensorValues = Object.values(currentSensors) as string[];
    
    // Find removed, added, and unchanged sensors
    const removed = currentSensorValues.filter(sensor => !newSensors.includes(sensor));
    const added = newSensors.filter(sensor => !currentSensorValues.includes(sensor));
    const unchanged = currentSensorValues.filter(sensor => newSensors.includes(sensor));

    return {
      hasChanges: removed.length > 0 || added.length > 0,
      details: {
        removed,
        added,
        unchanged
      }
    };
  };

  // Komponent do wyświetlania zmian w sensorach
  const SensorChangesDisplay = ({ currentValue, newValue }: { currentValue: any, newValue: string[] }) => {
    const comparison = compareSensors(currentValue, newValue);
    
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {comparison.details.removed.length > 0 && (
          <Box>
            <Typography variant="caption" color="error">
              Removed:
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {comparison.details.removed.map((sensor, idx) => (
                <Chip
                  key={idx}
                  label={sensor}
                  size="small"
                  color="error"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        )}
        {comparison.details.added.length > 0 && (
          <Box>
            <Typography variant="caption" color="success.main">
              Added:
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {comparison.details.added.map((sensor, idx) => (
                <Chip
                  key={idx}
                  label={sensor}
                  size="small"
                  color="success"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        )}
        {comparison.details.unchanged.length > 0 && (
          <Box>
            <Typography variant="caption" color="text.secondary">
              Unchanged:
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {comparison.details.unchanged.map((sensor, idx) => (
                <Chip
                  key={idx}
                  label={sensor}
                  size="small"
                  color="default"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  // Zmodyfikuj ValueDisplay, aby obsługiwał specjalny przypadek sensorów
  const ValueDisplay = ({ value, field }: { value: any, field: string }) => {
    if (field === 'sensors') {
      if (Array.isArray(value)) {
        return (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {value.map((sensor, idx) => (
              <Chip key={idx} label={sensor} size="small" />
            ))}
          </Box>
        );
      }
      return (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {Object.entries(value).map(([key, val]) => (
            <Chip key={key} label={`${val}`} size="small" />
          ))}
        </Box>
      );
    }

    // Dla wartości boolean
    if (typeof value === 'boolean' || value === 'true' || value === 'false') {
      const boolValue = String(value).toLowerCase() === 'true';
      return (
        <Chip
          size="small"
          icon={boolValue ? <CheckIcon /> : <CloseIcon />}
          label={boolValue ? "Yes" : "No"}
          color={boolValue ? "success" : "default"}
        />
      );
    }

    // Dla obiektów (np. antenna_1)
    if (typeof value === 'object' && !Array.isArray(value)) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {Object.entries(value).map(([key, val]) => (
            <Box key={key} sx={{ display: 'flex', gap: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {key}:
              </Typography>
              <Typography variant="caption">
                {String(val)}
              </Typography>
            </Box>
          ))}
        </Box>
      );
    }

    // Dla tablic
    if (Array.isArray(value)) {
      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {value.map((item, index) => (
            <Chip
              key={index}
              label={String(item)}
              size="small"
              variant="outlined"
            />
          ))}
        </Box>
      );
    }

    // Dla zwykłych wartości
    return <Typography>{String(value)}</Typography>;
  };

  const CompareChangesModal = () => {
    // Funkcja sprawdzająca, czy pole faktycznie się zmieniło
    const hasFieldChanged = (currentValue: any, newValue: any) => {
      if (typeof currentValue === 'object' || typeof newValue === 'object') {
        return JSON.stringify(currentValue) !== JSON.stringify(newValue);
      }
      return String(currentValue).toLowerCase() !== String(newValue).toLowerCase();
    };

    return (
      <Dialog
        open={isCompareModalOpen}
        onClose={() => setIsCompareModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider' }}>
          Review Changes
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {changes.length === 0 ? (
            <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
              No changes detected for the selected devices.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {changes.map((deviceChange) => (
                <Paper key={deviceChange.box} variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6">
                      Device #{deviceChange.box}
                    </Typography>
                    <Chip
                      label={`${Object.keys(deviceChange).length - 1} changes`}
                      color="primary"
                      size="small"
                    />
                  </Box>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Field</TableCell>
                          <TableCell>Current Value</TableCell>
                          <TableCell>New Value</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedFields.map(field => {
                          const backendField = correctFieldNames[field] || field;
                          const currentValue = boxDetails.find(b => b.box === deviceChange.box)?.[backendField];
                          const newValue = values[field];
                          const fieldChanged = field === 'sensors' 
                            ? compareSensors(currentValue, newValue).hasChanges 
                            : hasFieldChanged(currentValue, newValue);

                          return (
                            <TableRow
                              key={field}
                              sx={{
                                backgroundColor: fieldChanged ? 'action.hover' : 'inherit'
                              }}
                            >
                              <TableCell sx={{ textTransform: 'capitalize' }}>
                                {field.replace(/([A-Z])/g, ' $1').trim()}
                              </TableCell>
                              <TableCell>
                                {field === 'sensors' ? (
                                  <ValueDisplay value={currentValue} field={field} />
                                ) : (
                                  <ValueDisplay value={currentValue} field={field} />
                                )}
                              </TableCell>
                              <TableCell>
                                {field === 'sensors' ? (
                                  <SensorChangesDisplay currentValue={currentValue} newValue={newValue} />
                                ) : (
                                  <ValueDisplay value={newValue} field={field} />
                                )}
                              </TableCell>
                              <TableCell>
                                {fieldChanged ? (
                                  <Chip
                                    label="Will change"
                                    color="warning"
                                    size="small"
                                  />
                                ) : (
                                  <Chip
                                    label="No change"
                                    color="default"
                                    size="small"
                                    variant="outlined"
                                  />
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          p: 2, 
          borderTop: 1, 
          borderColor: 'divider',
          gap: 1 
        }}>
          <Button 
            onClick={() => setIsCompareModalOpen(false)}
            color="inherit"
          >
            Cancel
          </Button>
          {changes.length > 0 && (
            <Button
              variant="contained"
              onClick={() => {
                setIsCompareModalOpen(false);
                handleSubmit();
              }}
              disabled={isSubmitting}
              color="success"
              startIcon={isSubmitting ? <CircularProgress size={20} /> : <CheckIcon />}
            >
              {isSubmitting ? 'Updating...' : 'Confirm Changes'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  // Display missing boxes in the UI
  const renderMissingBoxes = () => {
    if (missingBoxes.length > 0) {
      return (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="error">
            Missing Boxes:
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {missingBoxes.map((box, index) => (
              <Chip key={index} label={box} color="error" variant="outlined" />
            ))}
          </Box>
        </Box>
      );
    }
    return null;
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ 
        mb: 4, 
        display: 'flex', 
        alignItems: 'center',
        borderBottom: 1,
        borderColor: 'divider',
        pb: 2
      }}>
        <IconButton 
          onClick={() => navigate(-1)} 
          sx={{ 
            mr: 2,
            bgcolor: 'background.secondary',
            '&:hover': {
              bgcolor: isDark ? 'background.paper' : 'primary.light',
            }
          }}
        >
          <ArrowBack />
        </IconButton>
        <Typography variant="h4">Bulk Edit Devices</Typography>
      </Box>

      <Paper sx={{ 
        ...paperStyle,
        mb: 3,
      }}>
        <Stepper 
          activeStep={activeStep} 
          sx={{ 
            mb: 4,
            '& .MuiStepLabel-label': {
              color: isDark ? 'text.secondary' : 'inherit'
            },
            '& .MuiStepIcon-root.Mui-active': {
              color: isDark ? '#64B5F6' : 'primary.main'
            }
          }}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box sx={{ mt: 4, mb: 4 }}>
          {renderStepContent(activeStep)}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
          <Button
            disabled={activeStep === 0 || isSubmitting}
            onClick={handleBack}
          >
            Back
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {activeStep === steps.length - 1 ? (
              <Button
                variant="contained"
                onClick={fetchBoxDetails}
                disabled={!deviceNumbers.trim() || isLoading}
                startIcon={isLoading ? <CircularProgress size={20} /> : <Compare />}
              >
                {isLoading ? 'Checking...' : 'Review Changes'}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={
                  (activeStep === 0 && selectedFields.length === 0) ||
                  (activeStep === 1 && Object.keys(values).length === 0)
                }
              >
                Next
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      <CompareChangesModal />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity}
          sx={{ 
            width: '100%',
            borderRadius: 0,
            '& .MuiAlert-icon': {
              color: snackbar.severity === 'success' ? '#4caf50' : '#f44336'
            }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      {renderMissingBoxes()}
    </Container>
  );
};

export default BulkEditDevices;